import type { PlateBlock, PlateSegment } from './types'
import {
  align,
  code,
  escapeHtml,
  groupBy,
  heading,
  htmlAttr,
  inlineItem,
  paragraph,
  quote,
  separator,
  table,
  textToHtml,
  wp,
} from './utils'

const INLINE_FORMATS = [
  ['code', 'code'],
  ['strikethrough', 's'],
  ['underline', 'u'],
  ['italic', 'em'],
  ['bold', 'strong'],
] as const
const NESTED_BLOCK_IGNORED_TYPES = new Set(['a', 'code_line', 'column'])

const firstSegment = (block: PlateBlock) => block[0]

const tag = (name: string, html: string) => `<${name}>${html}</${name}>`

const linkUrl = (segment: PlateSegment) => inlineItem(segment, 'a')?.url

const inlineHtml = (segment: PlateSegment) => {
  let html = textToHtml(segment.text)

  INLINE_FORMATS.forEach(([key, tagName]) => {
    if (segment[key]) {
      html = tag(tagName, html)
    }
  })

  const url = linkUrl(segment)

  return url ? `<a href="${escapeHtml(url)}">${html}</a>` : html
}

const segmentsHtml = (segments: PlateSegment[]) =>
  segments.map(inlineHtml).join('')

const blockAlign = (block: PlateBlock) => align(firstSegment(block)?.align)

const toBlocks = (segments: PlateSegment[]) =>
  groupBy(segments, ({ blockIndex }) => blockIndex)

const nestedType = (segment: PlateSegment) =>
  segment.inline?.find(
    (item) => item.type && !NESTED_BLOCK_IGNORED_TYPES.has(item.type)
  )?.type || 'p'

const asColumnSegment = (segment: PlateSegment): PlateSegment => ({
  ...segment,
  type: nestedType(segment),
})

const columnId = (segment: PlateSegment, index: number) =>
  inlineItem(segment, 'column')?.id || `column-${index}`

const renderColumn = (segments: PlateSegment[]) =>
  wp(
    'column',
    `<div class="wp-block-column">${blocksHtml(
      toBlocks(segments.map(asColumnSegment))
    )}</div>`
  )

const renderColumns = (segments: PlateSegment[]) =>
  wp(
    'columns',
    `<div class="wp-block-columns">${groupBy(segments, columnId)
      .map(renderColumn)
      .join('')}</div>`
  )

const listType = (block: PlateBlock) =>
  String(firstSegment(block)?.listStyleType || '')

const isListBlock = (block: PlateBlock) => Boolean(listType(block))

const listKey = (block: PlateBlock) =>
  `${listType(block)}:${firstSegment(block)?.indent || 0}`

const listTag = (type: string) => {
  if (type === 'decimal') {
    return 'ol'
  }

  return 'ul'
}

const listAttrs = (tagName: string) => {
  if (tagName === 'ol') {
    return ' {"ordered":true}'
  }

  return ''
}

const todoCheckbox = (block: PlateBlock) => {
  if (listType(block) !== 'todo') {
    return ''
  }

  return `<input type="checkbox" disabled${
    firstSegment(block)?.checked ? ' checked' : ''
  } />`
}

const renderListItem = (block: PlateBlock) =>
  wp('list-item', `<li>${todoCheckbox(block)}${segmentsHtml(block)}</li>`)

const renderList = (blocks: PlateBlock[]) => {
  const tagName = listTag(listType(blocks[0]))
  const items = blocks.map(renderListItem).join('\n')

  return wp(
    'list',
    `<${tagName} class="wp-block-list">${items}</${tagName}>`,
    listAttrs(tagName)
  )
}

const takeList = (blocks: PlateBlock[], startIndex: number) => {
  const items: PlateBlock[] = []
  const key = listKey(blocks[startIndex])
  let index = startIndex

  while (index < blocks.length && listKey(blocks[index]) === key) {
    items.push(blocks[index])
    index += 1
  }

  return {
    items,
    nextIndex: index,
  }
}

const rowId = (segment: PlateSegment, index: number) =>
  inlineItem(segment, 'tr')?.id || `row-${index}`

const cellId = (segment: PlateSegment, index: number) =>
  inlineItem(segment, 'td')?.id || `cell-${index}`

const spanAttr = (name: string, value?: number) => {
  if (!value || value <= 1) {
    return ''
  }

  return htmlAttr(name, value)
}

const renderTableCell = (segments: PlateSegment[]) => {
  const cell = inlineItem(segments[0], 'td')

  return `<td${spanAttr('colspan', cell?.colSpan)}${spanAttr(
    'rowspan',
    cell?.rowSpan
  )}>${segmentsHtml(segments)}</td>`
}

const renderTableRow = (segments: PlateSegment[]) =>
  `<tr>${groupBy(segments, cellId).map(renderTableCell).join('')}</tr>`

const renderTable = (segments: PlateSegment[]) =>
  table(groupBy(segments, rowId).map(renderTableRow).join(''))

const renderBlock = (block: PlateBlock): string => {
  const type = String(firstSegment(block).type || '')
  const html = segmentsHtml(block)
  const textAlign = blockAlign(block)
  const headingHtml = heading(type, html, textAlign)

  if (type === 'column_group') {
    return renderColumns(block)
  }
  if (type === 'table') {
    return renderTable(block)
  }
  if (type === 'hr') {
    return separator()
  }
  if (headingHtml) {
    return headingHtml
  }
  if (type === 'blockquote') {
    return quote(html, textAlign)
  }
  if (type === 'code_block') {
    return code(block.map(({ text }) => text).join(''))
  }

  return paragraph(html, textAlign)
}

const blocksHtml = (blocks: PlateBlock[]) => {
  const html: string[] = []
  let index = 0

  while (index < blocks.length) {
    const block = blocks[index]

    if (!isListBlock(block)) {
      html.push(renderBlock(block))
      index += 1
      continue
    }

    const list = takeList(blocks, index)

    html.push(renderList(list.items))
    index = list.nextIndex
  }

  return html.join('\n')
}

export const plate = (segments: PlateSegment[]) =>
  blocksHtml(toBlocks(segments))
